import { Component, Input } from '@angular/core';
import { SituViewWebApiClient, Company, Product2, AssetTemplateProperties, PropertyType, GetAssetReferenceRecommendationsRequest, PublishAssetsRequest, AssetInstanceDetails, FileVaultAssetResource,
  GetAssetTemplateRequest, GetMeshDetailsRequest, GetMeshDetailsResponse, FileVaultReferenceRelation,
  GetFileVaultReferenceRelationsRequest,
  FileVaultCategory,
  AssetValidationRule,
  RuleResult} from 'situview-webapi-client';
import { AuthService, BreadcrumbService, ProductService, StateService } from 'src/app/services';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { INewProduct } from 'src/app/views';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AssetValidationComponent, SelectArtifactDialogComponent, SelectArtifactDialogData, SelectArtifactResultData } from 'src/app/components';

@Component({
  selector: 'product-publish-tab',
  templateUrl: './product-publish-tab.component.html',
  styleUrls: ['./product-publish-tab.component.scss']
})

export class ProductPublishTabComponent extends SituBaseComponent {
  @Input() product: INewProduct = new Product2();
  @Input() productCompany: Company | undefined;

  assetResources: Record<string, FileVaultReferenceRelation> = {};
  assetInstances: Record<string, AssetInstance> = {};
  assetTemplates: Record<string, AssetTemplate> = {};

  selectedAssetPropertyDetails?: AssetPropertyDetails;

  assetReferenceRecommendations: AssetReferenceRecommendation[] = [];
  newAssetReferenceRecommendations: NewAssetReferenceRecommendation[] = [];
  fileVaultReferenceRecommendations: FileVaultReferenceRecommendation[] = [];

  // The list of artifacts that have been created, but not yet commited to the publish queue
  newAssetInstances: AssetInstancePublishDetails[] = [];
  newAssetResources: FileVaultAssetResourcePublishDetails[] = [];

  // The list of artifacts that have been commited to the publish queue and are awaiting publishing
  publishQueueAssetInstances: AssetInstancePublishDetails[] = [];
  publishQueueAssetResources: FileVaultAssetResourcePublishDetails[] = [];

  assetInstanceSearchTerms?: string;

  isPublishing = false;
  publishComment?: string;

  companyCode?: string;
  newAssetResourceKeyStart?: string;
  newAssetInstanceKeyStart?: string;
  newAssetInstanceName?: string;
  newAssetInstanceTemplateName?: string;

  selectArtifactDialogRef: MatDialogRef<SelectArtifactDialogComponent> | undefined;

  confirmationOverridden = false;

  propertyType = PropertyType;

  readonly MAX_ASSET_NAME_LENGTH = 30;

  private _loading = 0;
  public get loading(): number { return this._loading; }

  public set loading(val: number) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val != 0);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private router: Router,
    private situViewClient: SituViewWebApiClient,
    private productService: ProductService,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private dialog: MatDialog) {
    super(authService, stateService);


  }

  ngOnInit(): void {
    super.ngOnInit();

    this.buildRecommendedAssetInstanceName();
    this.getProductTypeInfo();
    this.getNewAssetKeyInfo();
  }

  async getProductTypeInfo() {
    var productTypes = await this.productService.LoadProductTypes();
    var productType = productTypes.find(x => x.id === this.product.productTypeId);
    this.newAssetInstanceTemplateName = productType!.templateName;
  }

  getNewAssetKeyInfo() {
    this.companyCode = this.productCompany?.code?.toLowerCase();
    this.newAssetInstanceKeyStart = `assetinstance\\${this.companyCode}\\`;
    this.newAssetResourceKeyStart = `assetresource\\${this.companyCode}\\`;
  }

  buildRecommendedAssetInstanceName() {
    if (this.product.callerReferenceKey) {
      // CallerReferenceKey is already set, no need to go this.
      return;
    }

    var maxNameSize = this.MAX_ASSET_NAME_LENGTH;

    if (this.product.sku && this.product.sku.length >= maxNameSize - 15) {
      // Just the SKU by itself is big enough, just run with that
      this.newAssetInstanceName = this.product.sku.substring(0, maxNameSize);
      return;
    }

    var name = this.product.name!.replace(/\s+/g, '-');
    if (this.product.sku && this.product.name != this.product.sku) {
      var sku = `${this.product.sku}_`;
      var maxNameLength = maxNameSize - sku.length;
      name = this.reduceString(name, maxNameLength);
      name = `${sku}${name}`;
    } else {
      name = this.reduceString(name, maxNameSize);
    }

    this.newAssetInstanceName = name;
  }

  async getAssetTemplate(assetTemplateKey: string) {
    // See if it has already been loaded
    var assetTemplate = this.assetTemplates[assetTemplateKey];
    if (assetTemplate) {
      return assetTemplate;
    }
    
    this.loading++;

    try {
      var request = new GetAssetTemplateRequest();
      request.assetTemplateKey = assetTemplateKey;
      var result  = await this.situViewClient.getAssetTemplate(request);

      if (!result.success) {
        this.showMessage("Failed to load product asset infomation")
        return;
      }

      assetTemplate = { properties: result.assetTemplate?.map(x => x as AssetTemplateProperty) } as AssetTemplate;
      this.assetTemplates[assetTemplateKey] = assetTemplate;

      //console.log(this.assetTemplate);
    } finally {
      this.loading--;
    }

    return assetTemplate;
  }

  async onFindAndReplaceAssetReference(assetPropertyDetails: AssetPropertyDetails, assetInstanceSearchTerms = '') {
    //console.log(assetPropertyDetails);
    this.selectedAssetPropertyDetails = assetPropertyDetails;
    this.assetInstanceSearchTerms = assetInstanceSearchTerms;
    this.confirmationOverridden = false;

    this.newAssetInstances = [];
    this.newAssetResources = [];

    if (!assetPropertyDetails) {
      return;
    }

    await this.loadAssetReferenceRecommendations()
  }

  clearCurrentSelection() {
    this.selectedAssetPropertyDetails = undefined;
    this.assetInstanceSearchTerms = '';
    this.confirmationOverridden = false;

    this.newAssetInstances = [];
    this.newAssetResources = [];
  }

  onPropertyEdited(assetPropertyDetails: AssetPropertyDetails) {
    this.addAssetPropertyDetailsToPublishQueue(assetPropertyDetails);
  }

  async loadAssetReferenceRecommendations() {
    var assetPropertyDetails = this.selectedAssetPropertyDetails!;
    var request = new GetAssetReferenceRecommendationsRequest();
    request.companyId = this.productCompany!.id;
    request.productId = this.product.id;
    request.parentAssetInstanceKey = this.product.callerReferenceKey;
    request.assetType = assetPropertyDetails.assetPropertyTemplate.assetType;
    request.assetTemplateKey = assetPropertyDetails.assetPropertyTemplate.templateID;

    if (this.assetInstanceSearchTerms) {
      request.searchTerms = this.assetInstanceSearchTerms;
    }

    var response = await this.situViewClient.getAssetReferenceRecommendations(request);

    this.fileVaultReferenceRecommendations = [];
    if (response.fileVaultReferences) {
      for (let fileVaultReference of response.fileVaultReferences) {
        var fileVaultReferenceRecommendation = new FileVaultReferenceRecommendation();
        fileVaultReferenceRecommendation.fileVaultReferenceRelation = fileVaultReference;
        fileVaultReferenceRecommendation.isSelected = false;
        this.fileVaultReferenceRecommendations.push(fileVaultReferenceRecommendation);
      }
    }

    this.assetReferenceRecommendations = [];
    if (response.artifactRecommendations) {
      for (let callerReferenceKey of response.artifactRecommendations) {
        var assetReferenceRecommendation = new AssetReferenceRecommendation();
        assetReferenceRecommendation.callerReferenceKey = callerReferenceKey;

        if (this.selectedAssetPropertyDetails?.assetProperty)
        {
          assetReferenceRecommendation.isSelected = this.callerReferenceKeyIsEqual(this.selectedAssetPropertyDetails!.assetProperty.callerReferenceKey, callerReferenceKey);
        } else {
          assetReferenceRecommendation.isSelected = false;
        }
        
        this.assetReferenceRecommendations.push(assetReferenceRecommendation);
      }
    }

    // Also allow the user to select assets that are in the publish queue
    this.newAssetReferenceRecommendations = [];
    for (let publishQueueAssetResource of this.publishQueueAssetResources) {
      if (publishQueueAssetResource.assetType === assetPropertyDetails.assetPropertyTemplate.assetType &&
        !this.assetReferenceRecommendations.some(x => x.callerReferenceKey === publishQueueAssetResource.callerReferenceKey)
      )
      {
        var newAssetReferenceRecommendation = new NewAssetReferenceRecommendation();
        newAssetReferenceRecommendation.callerReferenceKey = publishQueueAssetResource.callerReferenceKey;
        newAssetReferenceRecommendation.fileVaultReferenceRelation = publishQueueAssetResource.fileVaultReferenceRelation;
        newAssetReferenceRecommendation.assetValidation = publishQueueAssetResource.assetValidation;
        this.newAssetReferenceRecommendations.push(newAssetReferenceRecommendation);
      }
    }

    for (let publishQueueAssetInstance of this.publishQueueAssetInstances) {
      if (publishQueueAssetInstance.assetType === assetPropertyDetails.assetPropertyTemplate.assetType &&
        publishQueueAssetInstance.assetInstance.templateName.toLowerCase() === assetPropertyDetails.assetPropertyTemplate.templateName!.toLowerCase() &&
        !this.newAssetReferenceRecommendations.some(x => x.callerReferenceKey === publishQueueAssetInstance.callerReferenceKey) &&
        !this.assetReferenceRecommendations.some(x => x.callerReferenceKey === publishQueueAssetInstance.callerReferenceKey)
      )
      {
        var newAssetReferenceRecommendation = new NewAssetReferenceRecommendation();
        newAssetReferenceRecommendation.callerReferenceKey = publishQueueAssetInstance.callerReferenceKey;
        this.newAssetReferenceRecommendations.push(newAssetReferenceRecommendation);
      }
    }

    //console.log(this.assetReferenceRecommendations);
  }

  async selectAssetReferenceRecommendation(selectedRecommendation: AssetReferenceRecommendation) {
    this.assetReferenceRecommendations.forEach(x => x.isSelected = false);
    this.newAssetReferenceRecommendations.forEach(x => x.isSelected = false);
    this.fileVaultReferenceRecommendations.forEach(x => x.isSelected = false);

    this.newAssetInstances = [];
    this.newAssetResources = [];

    if (selectedRecommendation?.callerReferenceKey) {
      selectedRecommendation.isSelected = true;

      var assetType = this.selectedAssetPropertyDetails?.assetPropertyTemplate.assetType;
      var propertyTemplateName = this.selectedAssetPropertyDetails?.assetPropertyTemplate.templateName;
      var assetTemplateName = this.selectedAssetPropertyDetails?.assetInstance.templateName;
      if (assetType === PropertyType.MESH && assetTemplateName?.toLowerCase() === 'product') {
        await this.selectAssetReferenceRecommendationProductMesh(selectedRecommendation);
      }
    }
  }

  selectNewAssetReferenceRecommendation(selectedRecommendation: NewAssetReferenceRecommendation) {
    this.assetReferenceRecommendations.forEach(x => x.isSelected = false);
    this.newAssetReferenceRecommendations.forEach(x => x.isSelected = false);
    this.fileVaultReferenceRecommendations.forEach(x => x.isSelected = false);

    this.newAssetInstances = [];
    this.newAssetResources = [];

    selectedRecommendation.isSelected = true;
  }

  async selectAssetReferenceRecommendationProductMesh(selectedRecommendation: AssetReferenceRecommendation) {

    if (!selectedRecommendation.meshDetails) {
      selectedRecommendation.meshDetails = await this.getMeshDetails(this.productCompany?.id, undefined, selectedRecommendation.callerReferenceKey);
    }
    
    if (selectedRecommendation.meshDetails) {
      var materialsAssets = Object.entries(selectedRecommendation.meshDetails.materialsAssets!);
      for (let [assetName, assetInstance] of materialsAssets) {
        var assetInstanceName = assetName.trim().toLowerCase();
        assetInstanceName = this.reduceString(assetInstanceName, this.MAX_ASSET_NAME_LENGTH);

        var newMaterialAssetInstance = new AssetInstancePublishDetails();
        newMaterialAssetInstance.assetType = PropertyType.TEMPLATE;
        newMaterialAssetInstance.name = assetInstanceName;
        newMaterialAssetInstance.callerReferenceKey = `${this.newAssetInstanceKeyStart}material\\${assetInstanceName}`;
        newMaterialAssetInstance.assetInstance = assetInstance as AssetInstance;
        newMaterialAssetInstance.isSelected = false;
        this.newAssetInstances.push(newMaterialAssetInstance);
      }
    }
  }

  async selectFileVaultReferenceRecommendation(selectedRecommendation: FileVaultReferenceRecommendation) {
    this.assetReferenceRecommendations.forEach(x => x.isSelected = false);
    this.newAssetReferenceRecommendations.forEach(x => x.isSelected = false);

    var assetType = this.selectedAssetPropertyDetails?.assetPropertyTemplate.assetType;
    var propertyTemplateName = this.selectedAssetPropertyDetails?.assetPropertyTemplate.templateName;
    var assetTemplateName = this.selectedAssetPropertyDetails?.assetInstance.templateName;
    if (assetType === PropertyType.MESH && assetTemplateName?.toLowerCase() === 'product') {
      await this.selectFileVaultReferenceRecommendationForProductMesh(selectedRecommendation);
    } else if (assetType === PropertyType.TEMPLATE && propertyTemplateName?.toLowerCase() === "material") {
      this.selectFileVaultReferenceRecommendationForProductMaterials(selectedRecommendation)
    } else if (assetType === PropertyType.TEMPLATE && propertyTemplateName?.toLowerCase() === "texture") {
      this.selectFileVaultReferenceRecommendationForTextureAssetInstance(selectedRecommendation)
    } else if (assetType === PropertyType.MESH || assetType === PropertyType.IMAGE || assetType === PropertyType.BLENDER) {
      this.selectFileVaultReferenceRecommendationForAssetResources(selectedRecommendation);
    } 
  }

  async selectFileVaultReferenceRecommendationForProductMesh(selectedRecommendation: FileVaultReferenceRecommendation) {

    this.fileVaultReferenceRecommendations.forEach(x => 
      {
        if (x.fileVaultReferenceRelation !== selectedRecommendation.fileVaultReferenceRelation) {
          x.isSelected = false;
        }
      }
    );

    selectedRecommendation.isSelected = !selectedRecommendation.isSelected;

    this.newAssetInstances = [];
    this.newAssetResources = [];

    var assetType = this.selectedAssetPropertyDetails?.assetPropertyTemplate.assetType!;

    if (selectedRecommendation.isSelected) {
      var assetResourceName = this.buildAssetResourceKeyFromFileName(selectedRecommendation.fileVaultReferenceRelation.fileName!);
      assetResourceName = this.reduceString(assetResourceName, this.MAX_ASSET_NAME_LENGTH);

      var newFileVaultAssetResource = new FileVaultAssetResourcePublishDetails();
      newFileVaultAssetResource.assetType = assetType;
      newFileVaultAssetResource.name = assetResourceName;
      newFileVaultAssetResource.callerReferenceKey = `${this.newAssetResourceKeyStart}mesh\\${assetResourceName}`;
      newFileVaultAssetResource.fileVaultReferenceRelation = selectedRecommendation.fileVaultReferenceRelation;
      newFileVaultAssetResource.assetValidation = selectedRecommendation.assetValidation;
      newFileVaultAssetResource.selectionDisabled = true;
      this.newAssetResources.push(newFileVaultAssetResource);

      if (!selectedRecommendation.meshDetails) {
        selectedRecommendation.meshDetails = await this.getMeshDetails(selectedRecommendation.fileVaultReferenceRelation.companyId, selectedRecommendation.fileVaultReferenceRelation.id, undefined);
      }
      
      newFileVaultAssetResource.meshDetails = selectedRecommendation.meshDetails;

      if (newFileVaultAssetResource.meshDetails) {
        var materialsAssets = Object.entries(newFileVaultAssetResource.meshDetails.materialsAssets!);
        for (let [assetName, assetInstance] of materialsAssets) {
          var assetInstanceName = assetName.trim().toLowerCase();
          assetInstanceName = this.reduceString(assetInstanceName, this.MAX_ASSET_NAME_LENGTH);

          var newMaterialAssetInstance = new AssetInstancePublishDetails();
          newMaterialAssetInstance.assetType = PropertyType.TEMPLATE;
          newMaterialAssetInstance.name = assetInstanceName;
          newMaterialAssetInstance.callerReferenceKey = `${this.newAssetInstanceKeyStart}material\\${assetInstanceName}`;
          newMaterialAssetInstance.assetInstance = assetInstance as AssetInstance;
          newMaterialAssetInstance.isSelected = false;
          this.newAssetInstances.push(newMaterialAssetInstance);
        }
      }
    }
    
  }

  selectFileVaultReferenceRecommendationForProductMaterials(selectedRecommendation: FileVaultReferenceRecommendation) {
    
    var mapTypeHint = this.getTextureFileNameHint(selectedRecommendation.fileVaultReferenceRelation.fileName!);
    this.fileVaultReferenceRecommendations.forEach(x => 
      {
        if (x.isSelected && x.fileVaultReferenceRelation !== selectedRecommendation.fileVaultReferenceRelation) {
          var xHint = this.getTextureFileNameHint(x.fileVaultReferenceRelation.fileName!)
          if (xHint === mapTypeHint) {
            x.isSelected = false;
            var assetResourceName = this.buildAssetResourceKeyFromFileName(x.fileVaultReferenceRelation.fileName!);
            this.newAssetResources = this.newAssetResources.filter(e => e.name !== assetResourceName);
            this.newAssetInstances = this.newAssetInstances.filter(e => e.name !== assetResourceName);
          }
        }
      }
    );

    selectedRecommendation.isSelected = !selectedRecommendation.isSelected;
    var assetResourceNameWithoutHint = this.buildAssetResourceKeyFromFileNameWithoutMaterialHint(selectedRecommendation.fileVaultReferenceRelation.fileName!);
    assetResourceNameWithoutHint = this.reduceString(assetResourceNameWithoutHint, this.MAX_ASSET_NAME_LENGTH - mapTypeHint.length);
    var assetResourceName = `${assetResourceNameWithoutHint}${mapTypeHint}`;

    if (selectedRecommendation.isSelected) {
      // Add the new texture asset resource
      var newFileVaultAssetResource = new FileVaultAssetResourcePublishDetails();
      newFileVaultAssetResource.assetType = PropertyType.IMAGE;
      newFileVaultAssetResource.name = assetResourceName;
      newFileVaultAssetResource.callerReferenceKey = `${this.newAssetResourceKeyStart}texture\\${assetResourceName}`;
      newFileVaultAssetResource.fileVaultReferenceRelation = selectedRecommendation.fileVaultReferenceRelation;
      newFileVaultAssetResource.assetValidation = selectedRecommendation.assetValidation;
      this.newAssetResources.push(newFileVaultAssetResource);

      // Add the new texture asset instances 
      var textureAssetInstance = this.createNewAssetInstance(assetResourceName, 'texture', true);
      textureAssetInstance.assetInstance.values['image'] = newFileVaultAssetResource.callerReferenceKey;
      textureAssetInstance.assetInstance.values['IsNormal'] = mapTypeHint.toLowerCase() === '_n';
      textureAssetInstance.assetInstance.values['Manufacturer'] = this.companyCode;
      this.newAssetInstances.push(textureAssetInstance);

      // Add the material asset instances
      var materialAssetInstancePublishDetails = this.newAssetInstances.find(x => x.assetInstance.templateName.toLowerCase() === 'material');

      if (!materialAssetInstancePublishDetails &&
        this.selectedAssetPropertyDetails?.assetProperty?.callerReferenceKey &&
        this.selectedAssetPropertyDetails.assetProperty.callerReferenceKey != 'NA') {
        var materialAssetInstancePublishDetails = this.publishQueueAssetInstances.find(x => x.callerReferenceKey === this.selectedAssetPropertyDetails?.assetProperty.callerReferenceKey);
      }

      if (!materialAssetInstancePublishDetails) {
        materialAssetInstancePublishDetails = this.createNewAssetInstance(assetResourceNameWithoutHint, 'material', true);
        this.newAssetInstances.push(materialAssetInstancePublishDetails);
      }

      textureAssetInstance.confirmAction = () => { this.setTextureToMaterialAssetInstance(materialAssetInstancePublishDetails!.assetInstance, textureAssetInstance.callerReferenceKey) };

    } else {
      this.newAssetResources = this.newAssetResources.filter(e => e.name !== assetResourceName);
      this.newAssetInstances = this.newAssetInstances.filter(e => e.name !== assetResourceName);
    }
  }

  setTextureToMaterialAssetInstance(materialAssetInstance: AssetInstance, callerReferenceKey: string) {

    if (callerReferenceKey.endsWith('_a')) {
      materialAssetInstance.values['Albedo'] = { callerReferenceKey } as AssetReferenceValue;
      materialAssetInstance.values['RenderingMode'] = 'Opaque';
    } else if (callerReferenceKey.endsWith('_at')) {
      materialAssetInstance.values['Albedo'] = { callerReferenceKey } as AssetReferenceValue;
      materialAssetInstance.values['RenderingMode'] = 'Cutout';
    } else if (callerReferenceKey.endsWith('_n')) {
      materialAssetInstance.values['Normal Map'] = { callerReferenceKey } as AssetReferenceValue;
    } else if (callerReferenceKey.endsWith('_ms')) {
      materialAssetInstance.values['Metallic Map'] = { callerReferenceKey } as AssetReferenceValue;
    } else if (callerReferenceKey.endsWith('_ars')) {
      materialAssetInstance.values['Anisotropic Map'] = { callerReferenceKey } as AssetReferenceValue;
    } else if (callerReferenceKey.endsWith('_e')) {
      materialAssetInstance.values['Emission Map'] = { callerReferenceKey } as AssetReferenceValue;
      materialAssetInstance.values['Emission'] = true;
    } else if (callerReferenceKey.endsWith('_tr')) {
      materialAssetInstance.values['Transmission Map'] = { callerReferenceKey } as AssetReferenceValue;
    } else if (callerReferenceKey.endsWith('_ss')) {
      materialAssetInstance.values['Subsurface Scattering Map'] = { callerReferenceKey } as AssetReferenceValue;
    } else if (callerReferenceKey.endsWith('_shw')) {
      materialAssetInstance.values['Sheen Weight Map'] = { callerReferenceKey } as AssetReferenceValue;
    } else if (callerReferenceKey.endsWith('_shr')) {
      materialAssetInstance.values['Sheen Roughness Map'] = { callerReferenceKey } as AssetReferenceValue;
    } else if (callerReferenceKey.endsWith('_sht')) {
      materialAssetInstance.values['Sheen Tint Map'] = { callerReferenceKey } as AssetReferenceValue;
    } else if (callerReferenceKey.endsWith('_h')) {
      materialAssetInstance.values['Height Map'] = { callerReferenceKey } as AssetReferenceValue;
    } else if (callerReferenceKey.endsWith('_o')) {
      materialAssetInstance.values['Occlusion'] = { callerReferenceKey } as AssetReferenceValue;
    } else if (callerReferenceKey.endsWith('_ad')) {
      materialAssetInstance.values['Albedo Detail'] = { callerReferenceKey } as AssetReferenceValue;
    } else if (callerReferenceKey.endsWith('_nd')) {
      materialAssetInstance.values['Normal Map Detail'] = { callerReferenceKey } as AssetReferenceValue;
    } else {
        //fallback, assign to albedo
        materialAssetInstance.values["Albedo"] = { callerReferenceKey } as AssetReferenceValue;
    }

    //console.log(materialAssetInstance);
  }

  propertyNameToTextureSuffix(propertyName: string): string[] {
    if (propertyName === 'Albedo') {
      return ['_a', '_at']
    } else if (propertyName === 'Normal Map') {
      return ['_n']
    } else if (propertyName === 'Metallic Map') {
      return ['_ms']
    } else if (propertyName === 'Anisotropic Map') {
      return ['_ars']
    } else if (propertyName === 'Emission Map') {
      return ['_e']
    } else if (propertyName === 'Transmission Map') {
      return ['_tr']
    } else if (propertyName === 'Subsurface Scattering Map') {
      return ['_ss']
    } else if (propertyName === 'Sheen Weight Map') {
      return ['_shw']
    } else if (propertyName === 'Sheen Roughness Map') {
      return ['_shr']
    } else if (propertyName === 'Sheen Tint Map') {
      return ['_sht']
    } else if (propertyName === 'Height Map') {
      return ['_h']
    } else if (propertyName === 'Occlusion') {
      return ['_o']
    } else if (propertyName === 'Albedo Detail') {
      return ['_ad']
    } else if (propertyName === 'Normal Map Detail') {
      return ['_nd']
    }

    return []
  }

  onEnterPressNewAssetInstance() {
    if (this.newAssetInstanceName) {
      this.createAndSetNewAssetInstance();
    }
  }

  createAndSetNewAssetInstance() {
    if (this.selectedAssetPropertyDetails) {
      var newAssetInstance = this.createNewAssetInstance(this.newAssetInstanceName!, this.selectedAssetPropertyDetails.assetPropertyTemplate.templateName!);
      this.addAssetInstancePublishDetailsToPublishQueue(newAssetInstance);

      // Clear the object of it's previous properties, it might be replacing an embeded asset with a reference
      if (!('callerReferenceKey' in this.selectedAssetPropertyDetails.assetProperty)) {
        this.clearObject(this.selectedAssetPropertyDetails.assetProperty);
      }

      this.selectedAssetPropertyDetails.assetProperty.callerReferenceKey = newAssetInstance.callerReferenceKey;
      this.addAssetPropertyDetailsToPublishQueue(this.selectedAssetPropertyDetails);
    } else {
      var newAssetInstance = this.createNewAssetInstance(this.newAssetInstanceName!, this.newAssetInstanceTemplateName!);
      this.newAssetInstances.push(newAssetInstance);
      this.product.callerReferenceKey = newAssetInstance.callerReferenceKey;
    }

    this.newAssetInstanceName = undefined;
  }

  createNewAssetInstance(assetInstanceName: string, templateName: string, skipStringSizeReduction = false): AssetInstancePublishDetails {

    templateName = templateName.toLowerCase();
    assetInstanceName = assetInstanceName.trim().toLowerCase();

    if (!skipStringSizeReduction) {
      assetInstanceName = this.reduceString(assetInstanceName, this.MAX_ASSET_NAME_LENGTH);
    }
    
    var newAssetInstance = new AssetInstancePublishDetails();
    newAssetInstance.assetType = PropertyType.TEMPLATE
    newAssetInstance.name = assetInstanceName;
    newAssetInstance.callerReferenceKey = `${this.newAssetInstanceKeyStart}${templateName}\\${assetInstanceName}`;

    var assetInstance = new AssetInstance();
    assetInstance.templateID = `assettemplate\\situsystems\\${templateName}`;
    assetInstance.templateName = templateName;
    assetInstance.values = { };

    newAssetInstance.assetInstance = assetInstance;

    return newAssetInstance;
  }

  selectFileVaultReferenceRecommendationForTextureAssetInstance(selectedRecommendation: FileVaultReferenceRecommendation) {

    this.fileVaultReferenceRecommendations.forEach(x => 
      {
        if (x.fileVaultReferenceRelation !== selectedRecommendation.fileVaultReferenceRelation) {
          x.isSelected = false;
        }
      }
    );

    selectedRecommendation.isSelected = !selectedRecommendation.isSelected;

    this.newAssetInstances = [];
    this.newAssetResources = [];

    if (selectedRecommendation.isSelected) {
      var mapTypeHint = this.getTextureFileNameHint(selectedRecommendation.fileVaultReferenceRelation.fileName!);
      var assetResourceNameWithoutHint = this.buildAssetResourceKeyFromFileNameWithoutMaterialHint(selectedRecommendation.fileVaultReferenceRelation.fileName!);
      assetResourceNameWithoutHint = this.reduceString(assetResourceNameWithoutHint, this.MAX_ASSET_NAME_LENGTH - mapTypeHint.length);
      var assetResourceName = `${assetResourceNameWithoutHint}${mapTypeHint}`;

      // Add the new texture asset resource
      var newFileVaultAssetResource = new FileVaultAssetResourcePublishDetails();
      newFileVaultAssetResource.assetType = PropertyType.IMAGE;
      newFileVaultAssetResource.name = assetResourceName;
      newFileVaultAssetResource.callerReferenceKey = `${this.newAssetResourceKeyStart}texture\\${assetResourceName}`;
      newFileVaultAssetResource.fileVaultReferenceRelation = selectedRecommendation.fileVaultReferenceRelation;
      newFileVaultAssetResource.assetValidation = selectedRecommendation.assetValidation;
      this.newAssetResources.push(newFileVaultAssetResource);

      // Add the new texture asset instances 
      var textureAssetInstance = this.createNewAssetInstance(assetResourceName, 'texture');
      textureAssetInstance.assetInstance.values['image'] = newFileVaultAssetResource.callerReferenceKey;
      textureAssetInstance.assetInstance.values['IsNormal'] = mapTypeHint.toLowerCase() === '_n';
      textureAssetInstance.assetInstance.values['Manufacturer'] = this.companyCode;
      this.newAssetInstances.push(textureAssetInstance);
    }
    
  }


  selectFileVaultReferenceRecommendationForAssetResources(selectedRecommendation: FileVaultReferenceRecommendation) {

    this.fileVaultReferenceRecommendations.forEach(x => 
      {
        if (x.fileVaultReferenceRelation !== selectedRecommendation.fileVaultReferenceRelation) {
          x.isSelected = false;
        }
      }
    );

    selectedRecommendation.isSelected = !selectedRecommendation.isSelected;

    this.newAssetInstances = [];
    this.newAssetResources = [];

    var assetType = this.selectedAssetPropertyDetails?.assetPropertyTemplate.assetType!;

    if (selectedRecommendation.isSelected) {
      if (assetType === PropertyType.IMAGE) {
        var mapTypeHint = this.getTextureFileNameHint(selectedRecommendation.fileVaultReferenceRelation.fileName!);
        var assetResourceNameWithoutHint = this.buildAssetResourceKeyFromFileNameWithoutMaterialHint(selectedRecommendation.fileVaultReferenceRelation.fileName!);
        assetResourceNameWithoutHint = this.reduceString(assetResourceNameWithoutHint, this.MAX_ASSET_NAME_LENGTH - mapTypeHint.length);
        var assetResourceName = `${assetResourceNameWithoutHint}${mapTypeHint}`;
      } else {
        var assetResourceName = this.buildAssetResourceKeyFromFileName(selectedRecommendation.fileVaultReferenceRelation.fileName!);
      }
      
      var newFileVaultAssetResource = new FileVaultAssetResourcePublishDetails();
      newFileVaultAssetResource.assetType = assetType;
      newFileVaultAssetResource.name = assetResourceName;
      if (assetType === PropertyType.IMAGE) {
        newFileVaultAssetResource.callerReferenceKey = `${this.newAssetResourceKeyStart}texture\\${assetResourceName}`;
      } else if (assetType === PropertyType.MESH) {
        newFileVaultAssetResource.callerReferenceKey = `${this.newAssetResourceKeyStart}mesh\\${assetResourceName}`;
      } else if (assetType === PropertyType.BLENDER) {
        newFileVaultAssetResource.callerReferenceKey = `${this.newAssetResourceKeyStart}blender\\${assetResourceName}`;
      }
      newFileVaultAssetResource.fileVaultReferenceRelation = selectedRecommendation.fileVaultReferenceRelation;
      newFileVaultAssetResource.assetValidation = selectedRecommendation.assetValidation;
      this.newAssetResources.push(newFileVaultAssetResource);
    }
    
  }

  assetReferenceRecommendationIsSelected(): boolean {
    for (let assetReferenceRecommendation of this.assetReferenceRecommendations) {
      if (assetReferenceRecommendation.isSelected) {
          return true;
        }
    }

    for (let newAssetReferenceRecommendation of this.newAssetReferenceRecommendations) {
      if (newAssetReferenceRecommendation.isSelected) {
          return true;
        }
    }

    return false;
  }

  selectedAssetReferenceRecommendationIsDifferent(): boolean {
    for (let assetReferenceRecommendation of this.assetReferenceRecommendations) {
      if (assetReferenceRecommendation.isSelected && 
        !this.callerReferenceKeyIsEqual(assetReferenceRecommendation.callerReferenceKey, this.selectedAssetPropertyDetails!.assetProperty.callerReferenceKey)) {
          return true;
        }
    }

    for (let newAssetReferenceRecommendation of this.newAssetReferenceRecommendations) {
      if (newAssetReferenceRecommendation.isSelected &&
        !this.callerReferenceKeyIsEqual(newAssetReferenceRecommendation.callerReferenceKey, this.selectedAssetPropertyDetails!.assetProperty.callerReferenceKey)) {
          return true;
        }
    }

    return false;
  }

  fileVaultReferenceRecommendationsIsSelected(): boolean {
    for (let fileVaultReferenceRecommendation of this.fileVaultReferenceRecommendations) {
      if (fileVaultReferenceRecommendation.isSelected) {
          return true;
        }
    }

    return false;
  }

  newAssetResourcesHasFailures(): boolean {
    for (let newAssetResource of this.newAssetResources) {
      var worstValidationResult = AssetValidationComponent.getWorstAssetValidationIssueLevel(newAssetResource.assetValidation);
      if (worstValidationResult === RuleResult.Failure) {
          return true;
        }
    }

    return false;
  }

  selectedFileVaultReferenceRecommendationsIsDifferent(): boolean {
    for (let fileVaultReferenceRecommendation of this.fileVaultReferenceRecommendations) {
      if (fileVaultReferenceRecommendation.isSelected) {
          return true;
        }
    }

    return false;
  }

  confirmSelectedAssetReferenceRecommendation() {
    var selectedAssetReferenceRecommendation = this.assetReferenceRecommendations.find(x => x.isSelected);

    if (!selectedAssetReferenceRecommendation) {
      selectedAssetReferenceRecommendation = this.newAssetReferenceRecommendations.find(x => x.isSelected);
    }

    if (!selectedAssetReferenceRecommendation) {
      return;
    }
    
    // Clear the object of it's previous properties, it might be replacing an embeded asset with a reference
    if (!('callerReferenceKey' in this.selectedAssetPropertyDetails!.assetProperty)) {
      this.clearObject(this.selectedAssetPropertyDetails!.assetProperty);
    }

    // Set the selected properties caller reference key
    if (selectedAssetReferenceRecommendation?.callerReferenceKey) {
      this.selectedAssetPropertyDetails!.assetProperty.callerReferenceKey = selectedAssetReferenceRecommendation.callerReferenceKey;

      if (this.selectedAssetPropertyDetails!.assetInstance.templateName.toLowerCase() === "product" &&
          this.selectedAssetPropertyDetails!.assetPropertyTemplate.propertyName!.toLowerCase() === "materials") {
        // If the selected asset instance is a model, and we are setting a material, see if it has simmilar LODs that can be set.
        this.updateProductAssetMaterialLods(selectedAssetReferenceRecommendation.callerReferenceKey);
      }
    } else {
      this.selectedAssetPropertyDetails!.assetProperty.callerReferenceKey = undefined;
    }

    if (selectedAssetReferenceRecommendation.meshDetails) {
      this.updateProductMeshDetails(selectedAssetReferenceRecommendation.meshDetails);
    }

    // Add the editied asset instance to the publish queue if it is not already in the queue
    this.addAssetPropertyDetailsToPublishQueue(this.selectedAssetPropertyDetails!);

    for (let newAssetInstance of this.newAssetInstances) {
      if (!newAssetInstance.isSelected) {
        continue;
      }

      if (newAssetInstance.confirmAction) {
        newAssetInstance.confirmAction();
        newAssetInstance.confirmAction = undefined;
      }

      var callerReferenceKey = newAssetInstance.callerReferenceKey;
      this.assetInstances[callerReferenceKey] = newAssetInstance.assetInstance;
      
      if (newAssetInstance.meshDetails){
        var assetInstancesToAdd = Object.entries(newAssetInstance.meshDetails.materialsAssets!);
        
        for (let assetInstanceToAdd of assetInstancesToAdd) {
          this.assetInstances[assetInstanceToAdd[0]] = assetInstanceToAdd[1];
        }
      }

      this.addAssetInstancePublishDetailsToPublishQueue(newAssetInstance);
    }

    this.fileVaultReferenceRecommendations.forEach(x => x.isSelected = false);
    this.newAssetInstances = [];
    this.newAssetResources = [];
    this.selectedAssetPropertyDetails = undefined;
    this.confirmationOverridden = false;
  }

  addAssetPropertyDetailsToPublishQueue(assetPropertyDetails: AssetPropertyDetails) {
    // Add the editied asset instance to the publish queue if it is not already in the queue
    if (!this.publishQueueAssetInstances.some(x => x.callerReferenceKey === assetPropertyDetails!.assetInstanceKey)) {
      var newAssetInstance = new AssetInstancePublishDetails();
      newAssetInstance.callerReferenceKey = assetPropertyDetails!.assetInstanceKey;
      newAssetInstance.assetInstance = assetPropertyDetails!.assetInstance;
      this.publishQueueAssetInstances.push(newAssetInstance);
    }
  }

  addAssetInstancePublishDetailsToPublishQueue(assetInstancePublishDetails: AssetInstancePublishDetails) {
    // Add the editied asset instance to the publish queue if it is not already in the queue
    if (!this.publishQueueAssetInstances.some(x => x.callerReferenceKey === assetInstancePublishDetails!.callerReferenceKey)) {
      this.publishQueueAssetInstances.push(assetInstancePublishDetails);
    }
  }

  async confirmSelectedFileVaultReferenceRecommendation() {

    for (let newFileVaultAssetResource of this.newAssetResources) {
      if (!newFileVaultAssetResource.isSelected) {
        continue;
      }

      if (newFileVaultAssetResource.confirmAction) {
        newFileVaultAssetResource.confirmAction();
        newFileVaultAssetResource.confirmAction = undefined;
      }

      // Add the new asset reference to the publish queue if it is not already in the queue
      if (!this.publishQueueAssetResources.some(x => x.callerReferenceKey === newFileVaultAssetResource.callerReferenceKey)) {
        this.publishQueueAssetResources.push(newFileVaultAssetResource)
      }

      var callerReferenceKey = newFileVaultAssetResource.callerReferenceKey;
      this.assetResources[callerReferenceKey] = newFileVaultAssetResource.fileVaultReferenceRelation;

      if (this.selectedAssetPropertyDetails!.assetPropertyTemplate.assetType === newFileVaultAssetResource.assetType) {
        // Clear the object of it's previous properties, it might be replacing an embeded asset with a reference
        if (!('callerReferenceKey' in this.selectedAssetPropertyDetails!.assetProperty)) {
          this.clearObject(this.selectedAssetPropertyDetails!.assetProperty);
        }

        // This is the main asset resource that has been created and should be associated with the selectedAssetPropertyDetails 
        this.selectedAssetPropertyDetails!.assetProperty.callerReferenceKey = callerReferenceKey;
      }

      if (newFileVaultAssetResource.meshDetails)
      {
        this.updateProductMeshDetails(newFileVaultAssetResource.meshDetails);
      }
    }

    // Add the editied asset instance to the publish queue if it is not already in the queue
    this.addAssetPropertyDetailsToPublishQueue(this.selectedAssetPropertyDetails!);

    for (let newAssetInstance of this.newAssetInstances) {
      if (!newAssetInstance.isSelected) {
        continue;
      }

      if (newAssetInstance.confirmAction) {
        newAssetInstance.confirmAction();
        newAssetInstance.confirmAction = undefined;
      }

      // Add the new asset instances to the publish queue if it is not already in the queue
      this.addAssetInstancePublishDetailsToPublishQueue(newAssetInstance);

      var callerReferenceKey = newAssetInstance.callerReferenceKey;
      this.assetInstances[callerReferenceKey] = newAssetInstance.assetInstance;

      if (this.selectedAssetPropertyDetails!.assetPropertyTemplate.assetType === newAssetInstance.assetType && 
        this.selectedAssetPropertyDetails!.assetPropertyTemplate.templateName!.toLowerCase() === newAssetInstance.assetInstance.templateName!.toLowerCase()) {
        // This is the main asset instance that has been created and should be associated with the selectedAssetPropertyDetails 
        if (!this.selectedAssetPropertyDetails!.assetProperty) {
          this.selectedAssetPropertyDetails!.assetProperty = new AssetReferenceValue();
        }
    
        // Clear the object of it's previous properties, it might be replacing an embeded asset with a reference
        if (!('callerReferenceKey' in this.selectedAssetPropertyDetails!.assetProperty)) {
          this.clearObject(this.selectedAssetPropertyDetails!.assetProperty);
        }

        this.selectedAssetPropertyDetails!.assetProperty.callerReferenceKey = callerReferenceKey;
      }

      if (this.selectedAssetPropertyDetails!.assetInstance.templateName.toLowerCase() === "product" &&
          this.selectedAssetPropertyDetails!.assetPropertyTemplate.propertyName!.toLowerCase() === "materials") {
        // If the selected asset instance is a model, and we are setting a material, see if it has simmilar LODs that can be set.
        this.updateProductAssetMaterialLods(callerReferenceKey);
      }

      if (newAssetInstance.meshDetails){
        var assetInstancesToAdd = Object.entries(newAssetInstance.meshDetails.materialsAssets!);
            
        for (let assetInstanceToAdd of assetInstancesToAdd) {
          this.assetInstances[assetInstanceToAdd[0]] = assetInstanceToAdd[1];
        }
      }
    }

    this.fileVaultReferenceRecommendations.forEach(x => x.isSelected = false);
    this.newAssetInstances = [];
    this.newAssetResources = [];
    this.selectedAssetPropertyDetails = undefined;
    this.confirmationOverridden = false;
  }

  async getMeshDetails(companyId?: string, fileVaultReferenceId?: string, callerReferenceKey?: string): Promise<GetMeshDetailsResponse | undefined> {
    var request = new GetMeshDetailsRequest();
    request.companyId = companyId;
    request.fileVaultReferenceId = fileVaultReferenceId;
    request.callerReferenceKey = callerReferenceKey;
    var response = await this.situViewClient.getMeshDetails(request);

    if (!response.success) {
      console.error(response.errorMessages);
      this.showMessage("Failed to load Asset Meta Data");
      return undefined;
    }
    //console.log(response);

    return response;
  }

  updateProductAssetMaterialLods(callerReferenceKey: string) {
    var assetInstanceKey = this.product.callerReferenceKey!;
    var assetInstance = this.assetInstances[assetInstanceKey];

    var assetTemplate = this.assetTemplates[this.normalizeCallerReferenceKey(assetInstance.templateID)];
    var materialsProperty = assetTemplate.properties.find(x => x.propertyName?.toLowerCase() == 'materials');

    var subMeshes = assetInstance.values[materialsProperty!.propertyName!] as KeyValuePair[];

    var lodsToSet: AssetReferenceValue[] = [];
    for (let subMesh of subMeshes) {
      var materialName = subMesh.key!;
      if (!/LOD\d/.test(materialName)){
        continue;
      }

      var materials = subMesh.value as AssetReferenceValue[];
      var index = materials.findIndex(x => x.callerReferenceKey === callerReferenceKey);
      if (index === -1) {
        continue;
      }

      var normalizedName = materialName.replace(/LOD\d/, 'LOD');
      var matchingSubMeshes = subMeshes.filter(x => x.key!.replace(/LOD\d/, 'LOD') === normalizedName);
      for (let matchingSubMesh of matchingSubMeshes) {
        var matchingMaterials = matchingSubMesh.value as AssetReferenceValue[];
        var matchingMaterial = matchingMaterials[index];
        if (matchingMaterial && !matchingMaterial.callerReferenceKey) {
          lodsToSet.push(matchingMaterial);
        }
      }
    }

    for (let lodToSet of lodsToSet) {
      lodToSet.callerReferenceKey = callerReferenceKey;
    }
  }

  async updateProductMeshDetails(meshDetails: GetMeshDetailsResponse) {

    var assetInstanceKey = this.product.callerReferenceKey!;
    var assetInstance = this.assetInstances[assetInstanceKey];

    if (assetInstance.templateName.toLowerCase() !== "product") {
      return;
    }

    var assetTemplate = this.assetTemplates[this.normalizeCallerReferenceKey(assetInstance.templateID)];
    var materialsProperty = assetTemplate.properties.find(x => x.propertyName?.toLowerCase() == 'materials');

    var newSubMeshes: KeyValuePair[] = [];
    var existingSubMeshes = assetInstance.values[materialsProperty!.propertyName!] as KeyValuePair[];

    for (let mesh of meshDetails.meshes!) {
      var kvp = new KeyValuePair();
      kvp.key = `${mesh.name} (${mesh.subMeshes!.map(x => x.name).join(',')})`;
      kvp.value = [] as AssetReferenceValue[];
      newSubMeshes.push(kvp);

      // Get the currently set materials and see if they can be reused
      var existingSubMesh = existingSubMeshes.find(e => e.key?.startsWith(`${mesh.name} (`));
      var existingMaterialNames = existingSubMesh?.key?.match(/\((.*?)\)/)?.[1]?.split(",");
      var existingMaterials = existingSubMesh?.value as AssetReferenceValue[];

      for (let subMesh of mesh.subMeshes!) {
        var newKey = new AssetReferenceValue();
        kvp.value.push(newKey);
        if (meshDetails.materialsAssets && subMesh.name) {
          newKey.name = subMesh.name;
          var subMeshNameLower = subMesh.name.toLowerCase();
          var newAssetInstances = this.newAssetInstances.find(x => x.name.toLowerCase() === subMeshNameLower);
          if (newAssetInstances) {
            if (newAssetInstances.isSelected) {
              newKey.callerReferenceKey = newAssetInstances.callerReferenceKey;
            } else {
              var existingSubMeshIndex = existingMaterialNames?.indexOf(subMesh.name);
              newKey.callerReferenceKey = existingSubMeshIndex !== undefined ? existingMaterials?.[existingSubMeshIndex]?.callerReferenceKey : undefined;
            }
          }
        }
      }
    }

    assetInstance.values[materialsProperty!.propertyName!] = newSubMeshes;

    var slotsProperty = assetTemplate.properties.find(x => x.propertyName?.toLowerCase() == 'slots');
    assetInstance.values[slotsProperty!.propertyName!] = meshDetails.slotAssets;
  }

  buildAssetResourceKeyFromFileName(fileName: string): string{
    var assetResourceName = this.fileNameWithoutExtension(fileName);
    assetResourceName = assetResourceName.trim().toLowerCase();
    assetResourceName.replace('.', '_')

    return assetResourceName;
  }

  buildAssetResourceKeyFromFileNameWithoutMaterialHint(fileName: string): string {
    var fileName = this.fileNameWithoutExtension(fileName);
    fileName = fileName.trim().toLowerCase();
    var fileNameParts = fileName.split('_');
    if (fileNameParts.length > 1) {
      fileNameParts.pop(); // Remove the last part from the array
      fileName = fileNameParts.join('_');
    }

    fileName.replace('.', '_')

    return fileName;
  }

  fileNameWithoutExtension(fileName: string): string{
    var fileNameParts = fileName.split('.');
    if (fileNameParts.length > 1) {
      fileNameParts.pop(); // Remove the last part from the array
      fileName = fileNameParts.join('.');
    }

    return fileName;
  }

  buildCallerReferenceKeyNameWithoutMaterialHint(callerReferenceKey: string): string {
    var keyName = this.getCallerReferenceKeyName(callerReferenceKey);
    keyName = keyName.trim().toLowerCase();
    var fileNameParts = keyName.split('_');
    if (fileNameParts.length > 1) {
      fileNameParts.pop(); // Remove the last part from the array
      keyName = fileNameParts.join('_');
    }

    keyName.replace('.', '_')

    return keyName;
  }

  getCallerReferenceKeyName(callerReferenceKey: string): string{
    callerReferenceKey = this.normalizeCallerReferenceKey(callerReferenceKey);
    var callerReferenceKeyParts = callerReferenceKey.split('\\');
    if (callerReferenceKeyParts.length > 1) {
      return callerReferenceKeyParts.pop()!; // Remove the last part from the array
    }

    return callerReferenceKey;
  }

  getTextureFileNameHint(fileName: string): string {
    var hint = this.fileNameWithoutExtension(fileName);
    hint = hint.split('_').pop()!;
    hint = `_${hint}`;
    return hint;
  }

  normalizeCallerReferenceKey(a: string) : string {
    if (!a) {
      return a;
    }

    return a.replace(/\//g, '\\').toLowerCase();
  }

  callerReferenceKeyIsEqual(a: AssetInstanceValueTypes, b: AssetInstanceValueTypes) : boolean {
    if (!a || !b) {
      return false;
    }

    if (typeof a !== 'string' || typeof b !== 'string') {
      return false;
    }

    var isEqual = this.normalizeCallerReferenceKey(a) === this.normalizeCallerReferenceKey(b);
    return isEqual;
  }

  getAssetInstances(): [string, AssetInstance][] {
    return Object.entries(this.assetInstances);
  }

  getAssetResources(): [string, FileVaultReferenceRelation][] {
    return Object.entries(this.assetResources);
  }

  clearAssetInstance(assetInstanceKey: string) {
    delete this.assetInstances[assetInstanceKey];
  }

  clearAssetResource(assetResourceKey: string) {
    delete this.assetResources[assetResourceKey];
  }

  buildReadOnlyPublishComment(): string | undefined {
    var readOnlyComment: string | undefined = undefined;

    for (let assetResource of this.publishQueueAssetResources) {
      var worstValidationResult = AssetValidationComponent.getWorstAssetValidationIssueLevel(assetResource.assetValidation);
      if (assetResource.isSelected && worstValidationResult === RuleResult.Failure) {
        if (readOnlyComment){
          readOnlyComment = `${readOnlyComment}\n\n`;
        } else {
          readOnlyComment = 'Publishing with validation failures.\n\n';
        }

        readOnlyComment = `${readOnlyComment}${assetResource.name}`;
        
        for (let validationResult of assetResource.assetValidation) {
          if (validationResult.value.result! === RuleResult.Failure) {
            readOnlyComment = `${readOnlyComment}\n${validationResult.key}: ${validationResult.value.value}`;
            if (validationResult.value.valueUnit) {
              readOnlyComment = `${readOnlyComment}${validationResult.value.valueUnit}`;
            }
          }
        }
      }
    }

    return readOnlyComment;
  }

  async publishProductAsset() {
    this.loading++;
    this.isPublishing = true;

    try {
      this.clearCurrentSelection();

      var request = new PublishAssetsRequest();
      request.companyId = this.productCompany?.id;
      request.productId = this.product.id;
      request.assetInstancesToPublish = [];
      request.assetResourcesToPublish = [];

      for (let assetResource of this.publishQueueAssetResources) {
        if (!assetResource.isSelected) {
          continue;
        }
        var assetResourceDetails = new FileVaultAssetResource();
        assetResourceDetails.assetResourceKey = assetResource.callerReferenceKey;
        assetResourceDetails.fileVaultCompanyId = assetResource.fileVaultReferenceRelation.companyId;
        assetResourceDetails.fileVaultReferenceId = assetResource.fileVaultReferenceRelation.id;
        request.assetResourcesToPublish.push(assetResourceDetails);
      }

      for (let assetInstance of this.publishQueueAssetInstances) {
        if (!assetInstance.isSelected) {
          continue;
        }
        var assetInstanceDetails = new AssetInstanceDetails();
        assetInstanceDetails.assetInstanceKey = assetInstance.callerReferenceKey;
        assetInstanceDetails.assetInstance = assetInstance.assetInstance;
        request.assetInstancesToPublish.push(assetInstanceDetails);
      }

      request.productCallerReferenceKey = this.product.callerReferenceKey;

      var publishReadOnlyComment = this.buildReadOnlyPublishComment();
      if (this.publishComment || publishReadOnlyComment) {
        request.comment = [publishReadOnlyComment, this.publishComment]
          .filter(x => x)
          .join('\n')
          .trim();
      }
      
      var response = await this.situViewClient.publishAssets(request);
      
      if (!response.success) {
        console.error(response.errorMessages);
        this.showMessage("Failed to publish asset.");
        return;
      } else {
        this.showMessage("Successfully published asset.");
      }

      this.publishQueueAssetResources = this.publishQueueAssetResources.filter(x => !x.isSelected);
      this.publishQueueAssetInstances = this.publishQueueAssetInstances.filter(x => !x.isSelected);

      this.publishComment = undefined;
    } finally {
    this.loading--;
    this.isPublishing = false;
    }

    
  }

  selectExistingAssetInstance() {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
  
    const dialogWidth = `${Math.round((70 / 100) * screenWidth)}px`;
    const dialogHeight = `${Math.round((90 / 100) * screenHeight)}px`;

    var dialogData = new SelectArtifactDialogData();
    dialogData.multiSelect = false;
    dialogData.selectedTemplateName = this.newAssetInstanceTemplateName;

    this.selectArtifactDialogRef = this.dialog.open(SelectArtifactDialogComponent, {
      width: dialogWidth,
      height: dialogHeight,
      data: dialogData,
    });

    this._subscriptions.push(this.selectArtifactDialogRef.afterClosed()
      .subscribe((data: SelectArtifactResultData) => {
        if (data?.artifacts) {

          for (let i = 0; i < data.artifacts.length; i++) {
            const artifact = data.artifacts[i];
            if (i === 0) {
              this.product.callerReferenceKey = artifact.callerReferenceKey;
            }
          }
        }
      }));
  }

  canPublish() : boolean {
    var hasAssets = this.publishQueueAssetInstances.filter(x => x.isSelected).length > 0 || this.publishQueueAssetResources.filter(x => x.isSelected).length > 0;

    return hasAssets && !this.isPublishing;
  }

  async buildArtifactsFromAssets() {

    if (this.newAssetInstanceTemplateName === "product") {
      await this.buildProductArtifactFromAssets();
    } else if (
      this.newAssetInstanceTemplateName === "material") {
        await this.buildMaterialArtifactFromAssets();
    } else if (
      this.newAssetInstanceTemplateName === "brick") {
        await this.buildBrickArtifactFromAssets();
    }

    
  }

  async buildBrickArtifactFromAssets() {
    // Build the base string we will use to do matching with
    var productStrings = `${this.productCompany?.name} ${this.product.brand ?? ''} ${this.product.name} ${this.product.sku ?? ''}`.toLowerCase().split(/[^a-z0-9]+/);
    productStrings = productStrings.filter(str => !productStrings.some(otherStr => otherStr !== str && otherStr.includes(str)));
    var productStringMatches = new Set(productStrings);

    var assetInstance = this.assetInstances[this.product.callerReferenceKey!];
    var assetTemplate = this.assetTemplates['assettemplate\\situsystems\\brick'];
    
    var assetPropertyDetails = new AssetPropertyDetails;
    assetPropertyDetails.assetInstanceKey = this.product.callerReferenceKey!;
    assetPropertyDetails.assetInstance = assetInstance;
    assetPropertyDetails.assetTemplate = assetTemplate;

    // Add the brick materials
    var sides = ['End', 'Side', 'Top'];
    for (let side of sides) {
      var propertyName = `${side}Materials`

      var propertyCollection = [] as AssetReferenceValue[];
      assetInstance.values[propertyName!] = propertyCollection;
      assetPropertyDetails.assetPropertyTemplate = assetTemplate.properties.find(x => x.propertyName === propertyName)!;


      var foundAssets = false;
      var index = 0;
      do {
        foundAssets = false;
        
        index++;

        var property = new AssetReferenceValue();
        assetPropertyDetails.assetProperty = property;

        await this.onFindAndReplaceAssetReference(assetPropertyDetails);

        var fileNamePart = `_${side.toLowerCase()}_${index}`;
        var possibleBrickTextureAssetReferenceMatches = this.assetReferenceRecommendations.filter(x => x.callerReferenceKey!.endsWith(fileNamePart));
        var possibleBrickTextureNewAssetReferenceMatches = this.newAssetReferenceRecommendations.filter(x => x.callerReferenceKey!.endsWith(fileNamePart));
        var possibleBrickTextureFileVaultMatches = new Set(this.fileVaultReferenceRecommendations
          .map(x => this.buildAssetResourceKeyFromFileNameWithoutMaterialHint(x.fileVaultReferenceRelation.fileName!))
          .filter(x => x.endsWith(fileNamePart)));

        // Find the materials that most closly matches the name of the product etc
        var [bestBrickTextureAssetReferenceMatch, bestBrickTextureAssetReferenceMatchScore]  = this.findMostSimilarAssetReferenceRecommendations(possibleBrickTextureAssetReferenceMatches, productStringMatches);
        var [bestBrickTextureFileVaultMatch, bestBrickTextureFileVaultMatchScore] = this.findMostSimilarString(possibleBrickTextureFileVaultMatches, productStringMatches);
        var [bestBrickTextureNewAssetReferenceMatch, bestBrickTextureNewAssetReferenceMatchScore]  = this.findMostSimilarAssetReferenceRecommendations(possibleBrickTextureNewAssetReferenceMatches, productStringMatches);

        // As long as a match was found we want to prioritize whatever gets the highest score
        // In the case of a tie we want to prioritize, Existing Assets File Vault Files, and lastly New Assets that has been created this session
        if (bestBrickTextureAssetReferenceMatch &&
          bestBrickTextureAssetReferenceMatchScore >= bestBrickTextureNewAssetReferenceMatchScore &&
          bestBrickTextureAssetReferenceMatchScore >= bestBrickTextureFileVaultMatchScore
        ) {
          foundAssets = true;
          propertyCollection.push(property);

          await this.selectAssetReferenceRecommendation(bestBrickTextureAssetReferenceMatch);
          await this.confirmSelectedAssetReferenceRecommendation();

        } else if (bestBrickTextureFileVaultMatch &&
          bestBrickTextureFileVaultMatchScore >= bestBrickTextureAssetReferenceMatchScore &&
          bestBrickTextureFileVaultMatchScore >= bestBrickTextureNewAssetReferenceMatchScore
        ) {
          foundAssets = true;
          propertyCollection.push(property);

          var bestTextureFileVaultMatches = this.fileVaultReferenceRecommendations
            .filter(x => this.buildAssetResourceKeyFromFileNameWithoutMaterialHint(x.fileVaultReferenceRelation.fileName!) === bestBrickTextureFileVaultMatch);
          
          if (bestTextureFileVaultMatches.length > 0) {
            for (let bestTextureFileVaultMatch of bestTextureFileVaultMatches) {

              await this.selectFileVaultReferenceRecommendation(bestTextureFileVaultMatch);
            }
            
            await this.confirmSelectedFileVaultReferenceRecommendation();
          }

        } else if (bestBrickTextureNewAssetReferenceMatch &&
          bestBrickTextureNewAssetReferenceMatchScore >= bestBrickTextureAssetReferenceMatchScore &&
          bestBrickTextureNewAssetReferenceMatchScore >= bestBrickTextureFileVaultMatchScore
        ) {
          foundAssets = true;
          propertyCollection.push(property);

          await this.selectNewAssetReferenceRecommendation(bestBrickTextureNewAssetReferenceMatch);
          await this.confirmSelectedAssetReferenceRecommendation();

        }

        
      } while (foundAssets);
    }

    // Add the mortar materials
    assetPropertyDetails.assetPropertyTemplate = assetTemplate.properties.find(x => x.propertyName === "MortarMaterial")!;
    assetPropertyDetails.assetProperty = assetInstance.values['MortarMaterial'] as AssetReferenceValue;

    assetPropertyDetails.assetProperty.callerReferenceKey = undefined;

    await this.onFindAndReplaceAssetReference(assetPropertyDetails);

    var fileNamePart = `mortar`;
    productStringMatches.add(fileNamePart);

    var possibleMortarTextureAssetReferenceMatches = this.assetReferenceRecommendations.filter(x => x.callerReferenceKey!.endsWith(fileNamePart));
    var possibleMortarTextureNewAssetReferenceMatches = this.newAssetReferenceRecommendations.filter(x => x.callerReferenceKey!.endsWith(fileNamePart));
    var possibleMortarTextureFileVaultMatches = new Set(this.fileVaultReferenceRecommendations
      .map(x => this.buildAssetResourceKeyFromFileNameWithoutMaterialHint(x.fileVaultReferenceRelation.fileName!))
      .filter(x => x.endsWith(fileNamePart)));


    var [bestMortarTextureAssetReferenceMatch, bestMortarTextureAssetReferenceMatchScore] = this.findMostSimilarAssetReferenceRecommendations(possibleMortarTextureAssetReferenceMatches, productStringMatches);
    var [bestMortarTextureFileVaultMatch, bestMortarTextureFileVaultMatchScore] = this.findMostSimilarString(possibleMortarTextureFileVaultMatches, productStringMatches);
    var [bestMortarTextureNewAssetReferenceMatch, bestMortarTextureNewAssetReferenceMatchScore] = this.findMostSimilarAssetReferenceRecommendations(possibleMortarTextureNewAssetReferenceMatches, productStringMatches);

    // As long as a match was found we want to prioritize whatever gets the highest score
    // In the case of a tie we want to prioritize, Existing Assets File Vault Files, and lastly New Assets that has been created this session
    if (bestMortarTextureAssetReferenceMatch &&
      bestMortarTextureAssetReferenceMatchScore >= bestMortarTextureNewAssetReferenceMatchScore &&
      bestMortarTextureAssetReferenceMatchScore >= bestMortarTextureFileVaultMatchScore
    ) {
      await this.selectAssetReferenceRecommendation(bestMortarTextureAssetReferenceMatch);
      await this.confirmSelectedAssetReferenceRecommendation();

    } else if (bestMortarTextureFileVaultMatch &&
      bestMortarTextureFileVaultMatchScore >= bestMortarTextureAssetReferenceMatchScore &&
      bestMortarTextureFileVaultMatchScore >= bestMortarTextureNewAssetReferenceMatchScore
    ) {
      var bestTextureFileVaultMatches = this.fileVaultReferenceRecommendations
        .filter(x => this.buildAssetResourceKeyFromFileNameWithoutMaterialHint(x.fileVaultReferenceRelation.fileName!) === bestMortarTextureFileVaultMatch);
      
      if (bestTextureFileVaultMatches.length > 0) {
        for (let bestTextureFileVaultMatch of bestTextureFileVaultMatches) {

          await this.selectFileVaultReferenceRecommendation(bestTextureFileVaultMatch);
        }
        
        await this.confirmSelectedFileVaultReferenceRecommendation();
      }

    } else if (bestMortarTextureNewAssetReferenceMatch &&
      bestMortarTextureNewAssetReferenceMatchScore >= bestMortarTextureAssetReferenceMatchScore &&
      bestMortarTextureNewAssetReferenceMatchScore >= bestMortarTextureFileVaultMatchScore
    ) {
      await this.selectNewAssetReferenceRecommendation(bestMortarTextureNewAssetReferenceMatch);
      await this.confirmSelectedAssetReferenceRecommendation();

    }

    await this.getProductNotes();
  }

  async buildMaterialArtifactFromAssets() {
    // Build the base string we will use to do matching with
    var productStrings = `${this.productCompany?.name} ${this.product.brand ?? ''} ${this.product.name} ${this.product.sku ?? ''}`.toLowerCase().split(/[^a-z0-9]+/);
    productStrings = productStrings.filter(str => !productStrings.some(otherStr => otherStr !== str && otherStr.includes(str)));
    var productStringMatches = new Set(productStrings);

    var assetInstance = this.assetInstances[this.product.callerReferenceKey!];
    var assetTemplate = this.assetTemplates['assettemplate\\situsystems\\material'];
    

    var assetPropertyDetails = new AssetPropertyDetails;
    assetPropertyDetails.assetInstanceKey = this.product.callerReferenceKey!;
    assetPropertyDetails.assetInstance = assetInstance;
    assetPropertyDetails.assetTemplate = assetTemplate;

    for (let templateProperty of assetTemplate.properties) {
      if (templateProperty.assetType === PropertyType.TEMPLATE && templateProperty.templateName?.toLowerCase() === "texture") {

        assetPropertyDetails.assetPropertyTemplate = templateProperty;
        assetPropertyDetails.assetProperty = assetInstance.values[templateProperty.propertyName!] as AssetReferenceValue;

        assetPropertyDetails.assetProperty.callerReferenceKey = undefined;

        await this.onFindAndReplaceAssetReference(assetPropertyDetails);

        // Find the asset that most closly matches the name of the product etc
        var assetReferenceTextureNameStrings = new Set(this.assetReferenceRecommendations.map(x => this.buildCallerReferenceKeyNameWithoutMaterialHint(x.callerReferenceKey!)));
        var [bestTextureNameAssetReferenceMatch, bestTextureNameAssetReferenceMatchScore] = this.findMostSimilarString(assetReferenceTextureNameStrings, productStringMatches);
        var fileVaultReferenceTextureFileNameStrings = new Set(this.fileVaultReferenceRecommendations.map(x => this.buildAssetResourceKeyFromFileNameWithoutMaterialHint(x.fileVaultReferenceRelation.fileName!)));
        var [bestTextureFileNameFileVaultMatch, bestTextureFileNameFileVaultMatchScore] = this.findMostSimilarString(fileVaultReferenceTextureFileNameStrings, productStringMatches);
        var newAssetReferenceTextureNameStrings = new Set(this.newAssetReferenceRecommendations.map(x => this.buildCallerReferenceKeyNameWithoutMaterialHint(x.callerReferenceKey!)));
        var [bestTextureNameNewAssetReferenceMatch, bestTextureNameNewAssetReferenceMatchScore] = this.findMostSimilarString(newAssetReferenceTextureNameStrings, productStringMatches);

        // As long as a match was found we want to prioritize whatever gets the highest score
        // In the case of a tie we want to prioritize, Existing Assets, File Vault Files and lastly New Assets that has been created this session
        if (bestTextureNameAssetReferenceMatch &&
          bestTextureNameAssetReferenceMatchScore >= bestTextureNameNewAssetReferenceMatchScore &&
          bestTextureNameAssetReferenceMatchScore >= bestTextureFileNameFileVaultMatchScore
        ) {
          var suffixes = this.propertyNameToTextureSuffix(templateProperty.propertyName!);

          for (let suffix of suffixes) {
            var fullKeyName = `${bestTextureNameAssetReferenceMatch}${suffix}`;
            var bestTextureAssetReferenceMatch = this.assetReferenceRecommendations.find(x => x.callerReferenceKey!.endsWith(fullKeyName));

            if (bestTextureAssetReferenceMatch) {
              await this.selectAssetReferenceRecommendation(bestTextureAssetReferenceMatch);
              await this.confirmSelectedAssetReferenceRecommendation();
              continue;
            }
          }

        } else if (bestTextureFileNameFileVaultMatch &&
          bestTextureFileNameFileVaultMatchScore >= bestTextureNameAssetReferenceMatchScore &&
          bestTextureFileNameFileVaultMatchScore >= bestTextureNameNewAssetReferenceMatchScore
        ) {
          var suffixes = this.propertyNameToTextureSuffix(templateProperty.propertyName!);
          
          for (let suffix of suffixes) {
            var fileNameStart = `${bestTextureFileNameFileVaultMatch}${suffix}`;
            var bestTextureFileVaultMatch = this.fileVaultReferenceRecommendations.find(x => x.fileVaultReferenceRelation.fileName!.toLowerCase().startsWith(fileNameStart));

            if (bestTextureFileVaultMatch) {
              await this.selectFileVaultReferenceRecommendation(bestTextureFileVaultMatch);
              await this.confirmSelectedFileVaultReferenceRecommendation();
              continue;
            }
          }

        } else if (bestTextureNameNewAssetReferenceMatch &&
          bestTextureNameNewAssetReferenceMatchScore >= bestTextureNameAssetReferenceMatchScore &&
          bestTextureNameNewAssetReferenceMatchScore >= bestTextureFileNameFileVaultMatchScore
        ) {
          var suffixes = this.propertyNameToTextureSuffix(templateProperty.propertyName!);

          for (let suffix of suffixes) {
            var fullKeyName = `${bestTextureNameNewAssetReferenceMatch}${suffix}`;
            var bestTextureNewAssetReferenceMatch = this.newAssetReferenceRecommendations.find(x => x.callerReferenceKey!.endsWith(fullKeyName));

            if (bestTextureNewAssetReferenceMatch) {
              await this.selectNewAssetReferenceRecommendation(bestTextureNewAssetReferenceMatch);
              await this.confirmSelectedAssetReferenceRecommendation();
              continue;
            }
          }

        }
      }
    }

    this.fileVaultReferenceRecommendations.forEach(x => x.isSelected = false);
    this.newAssetInstances = [];
    this.newAssetResources = [];
    this.selectedAssetPropertyDetails = undefined;

    var notes = await this.getProductNotes();
    assetInstance.values["Notes"] = notes;
    if (notes) {
      this.processProductNotesForMaterial(notes, assetInstance);
    }

  }

  async buildProductArtifactFromAssets() {

    // Build the base string we will use to do matching with
    var productStrings = `${this.productCompany?.name} ${this.product.brand ?? ''} ${this.product.name} ${this.product.sku ?? ''}`.toLowerCase().split(/[^a-z0-9]+/);
    productStrings = productStrings.filter(str => !productStrings.some(otherStr => otherStr !== str && otherStr.includes(str)));
    var productStringMatches = new Set(productStrings);

    // First load up the options for Meshes

    var assetInstance = this.assetInstances[this.product.callerReferenceKey!];
    var assetTemplate = this.assetTemplates['assettemplate\\situsystems\\product'];

    var assetPropertyDetails = new AssetPropertyDetails;
    assetPropertyDetails.assetInstanceKey = this.product.callerReferenceKey!;
    assetPropertyDetails.assetInstance = assetInstance;
    assetPropertyDetails.assetTemplate = assetTemplate;
    assetPropertyDetails.assetProperty = assetInstance.values['Mesh'] as AssetReferenceValue;
    assetPropertyDetails.assetPropertyTemplate = assetTemplate.properties.find(x => x.propertyName === 'Mesh')!;

    assetPropertyDetails.assetProperty.callerReferenceKey = undefined;
    var hasEmbeddedTextures = true;

    await this.onFindAndReplaceAssetReference(assetPropertyDetails);

    // Find the mesh that most closly matches the name of the product etc
    var [bestMeshAssetReferenceMatch, bestMeshAssetReferenceMatchScore]  = this.findMostSimilarAssetReferenceRecommendations(this.assetReferenceRecommendations, productStringMatches);
    var [bestMeshFileVaultMatch, bestMeshFileVaultMatchScore] = this.findMostSimilarFileVaultReference(this.fileVaultReferenceRecommendations, productStringMatches);
    var [bestMeshNewAssetReferenceMatch, bestMeshNewAssetReferenceMatchScore]  = this.findMostSimilarAssetReferenceRecommendations(this.newAssetReferenceRecommendations, productStringMatches);

    // As long as a match was found we want to prioritize whatever gets the highest score
    // In the case of a tie we want to prioritize, Existing Assets File Vault Files, and lastly New Assets that has been created this session
    // If none of that works out, just pick the FBX file in the file vault if there is only one
    if (bestMeshAssetReferenceMatch &&
      bestMeshAssetReferenceMatchScore >= bestMeshNewAssetReferenceMatchScore &&
      bestMeshAssetReferenceMatchScore >= bestMeshFileVaultMatchScore
    ) {
      await this.selectAssetReferenceRecommendation(bestMeshAssetReferenceMatch);
      hasEmbeddedTextures = bestMeshAssetReferenceMatch.meshDetails?.hasEmbeddedTextures ?? false;
      var materialRecommendationAssetInstances = this.newAssetInstances.filter(x => !x.selectionDisabled);
      materialRecommendationAssetInstances.forEach(x => x.isSelected = false);
      await this.confirmSelectedAssetReferenceRecommendation();

    } else if (bestMeshFileVaultMatch &&
      bestMeshFileVaultMatchScore >= bestMeshAssetReferenceMatchScore &&
      bestMeshFileVaultMatchScore >= bestMeshNewAssetReferenceMatchScore
    ) {
      await this.selectFileVaultReferenceRecommendation(bestMeshFileVaultMatch);
      hasEmbeddedTextures = bestMeshFileVaultMatch.meshDetails?.hasEmbeddedTextures ?? false;
      var materialRecommendationAssetInstances = this.newAssetInstances.filter(x => !x.selectionDisabled);
      materialRecommendationAssetInstances.forEach(x => x.isSelected = false);
      await this.confirmSelectedFileVaultReferenceRecommendation();

    } else if (bestMeshNewAssetReferenceMatch &&
      bestMeshNewAssetReferenceMatchScore >= bestMeshAssetReferenceMatchScore &&
      bestMeshNewAssetReferenceMatchScore >= bestMeshFileVaultMatchScore
    ) {
      await this.selectNewAssetReferenceRecommendation(bestMeshNewAssetReferenceMatch);
      hasEmbeddedTextures = bestMeshNewAssetReferenceMatch.meshDetails?.hasEmbeddedTextures ?? false;
      var materialRecommendationAssetInstances = this.newAssetInstances.filter(x => !x.selectionDisabled);
      materialRecommendationAssetInstances.forEach(x => x.isSelected = false);
      await this.confirmSelectedAssetReferenceRecommendation();
      
    } else if (this.fileVaultReferenceRecommendations.length === 1) {
      await this.selectFileVaultReferenceRecommendation(this.fileVaultReferenceRecommendations[0]);
      hasEmbeddedTextures = this.fileVaultReferenceRecommendations[0].meshDetails?.hasEmbeddedTextures ?? false;
      var materialRecommendationAssetInstances = this.newAssetInstances.filter(x => !x.selectionDisabled);
      materialRecommendationAssetInstances.forEach(x => x.isSelected = false);
      await this.confirmSelectedFileVaultReferenceRecommendation();

    } else {
      this.showMessage("Failed to build Artifact: Suitable Mesh was not found.");
      this.selectedAssetPropertyDetails = undefined;
      return;
    }

    // Start working on assigning the materials if it does not have embedded textures 
    if (!hasEmbeddedTextures) {
      assetPropertyDetails.assetPropertyTemplate = assetTemplate.properties.find(x => x.propertyName === 'Materials')!;

      var materialList = assetInstance.values['Materials'] as KeyValuePair[];
      
      for (let material of materialList) {
        var subMaterialNames = material.key?.match(/\((.*?)\)/)?.[1]?.split(",") ?? [];
        var subMaterials = material.value as AssetReferenceValue[];

        // Work through the sub materials, using the sub material name to help with the match
        for (let i = 0; i < subMaterials.length; i++) {
          var subMaterialName = subMaterialNames[i]?.toLowerCase();
          var materialStrings = subMaterialName.split(/[^a-z0-9]+/);
          var filteredMaterialStringMatches = [...productStringMatches, ...materialStrings];
          filteredMaterialStringMatches = filteredMaterialStringMatches.filter(str => !filteredMaterialStringMatches.some(otherStr => otherStr !== str && otherStr.includes(str)));
          var materialStringMatches = new Set(filteredMaterialStringMatches);

          assetPropertyDetails.assetProperty = subMaterials[i];
          var origCallerReferenceKey = assetPropertyDetails.assetProperty.callerReferenceKey;
          assetPropertyDetails.assetProperty.callerReferenceKey = undefined;
          
          await this.onFindAndReplaceAssetReference(assetPropertyDetails, subMaterialName);

          // Find the asset that most closly matches the name of the material etc
          var assetReferenceTextureNameStrings = new Set(this.assetReferenceRecommendations.map(x => this.getCallerReferenceKeyName(x.callerReferenceKey!)));
          var [bestTextureNameAssetReferenceMatch, bestTextureNameAssetReferenceMatchScore] = this.findMostSimilarString(assetReferenceTextureNameStrings, materialStringMatches);
          var fileVaultReferenceTextureFileNameStrings = new Set(this.fileVaultReferenceRecommendations.map(x => this.buildAssetResourceKeyFromFileNameWithoutMaterialHint(x.fileVaultReferenceRelation.fileName!)));
          var [bestTextureFileNameFileVaultMatch, bestTextureFileNameFileVaultMatchScore] = this.findMostSimilarString(fileVaultReferenceTextureFileNameStrings, materialStringMatches);
          var newAssetReferenceTextureNameStrings = new Set(this.newAssetReferenceRecommendations.map(x => this.getCallerReferenceKeyName(x.callerReferenceKey!)));
          var [bestTextureNameNewAssetReferenceMatch, bestTextureNameNewAssetReferenceMatchScore] = this.findMostSimilarString(newAssetReferenceTextureNameStrings, materialStringMatches);
          var materialRecommendationTextureNameStrings = new Set(materialRecommendationAssetInstances.map(x => this.getCallerReferenceKeyName(x.callerReferenceKey!)));
          var [bestTextureNameMaterialRecommendationMatch, bestTextureNameMaterialRecommendationMatchScore] = this.findMostSimilarString(materialRecommendationTextureNameStrings, materialStringMatches);

          // As long as a match was found we want to prioritize whatever gets the highest score
          // In the case of a tie we want to prioritize, Existing Assets, File Vault Files and lastly New Assets that has been created this session
          if (bestTextureNameAssetReferenceMatch &&
            bestTextureNameAssetReferenceMatchScore >= bestTextureNameNewAssetReferenceMatchScore &&
            bestTextureNameAssetReferenceMatchScore >= bestTextureFileNameFileVaultMatchScore &&
            bestTextureNameAssetReferenceMatchScore >= bestTextureNameMaterialRecommendationMatchScore
          ) {
            var bestTextureAssetReferenceMatches = this.assetReferenceRecommendations
              .filter(x => this.getCallerReferenceKeyName(x.callerReferenceKey!) === bestTextureNameAssetReferenceMatch);

            if (bestTextureAssetReferenceMatches.length > 0) {
              for (let bestTextureAssetReferenceMatch of bestTextureAssetReferenceMatches) {
                //console.log(bestTextureAssetReferenceMatch.callerReferenceKey);
                await this.selectAssetReferenceRecommendation(bestTextureAssetReferenceMatch);
              }
              await this.confirmSelectedAssetReferenceRecommendation();
            }

          } else if (bestTextureFileNameFileVaultMatch &&
            bestTextureFileNameFileVaultMatchScore >= bestTextureNameAssetReferenceMatchScore &&
            bestTextureFileNameFileVaultMatchScore >= bestTextureNameNewAssetReferenceMatchScore &&
            bestTextureFileNameFileVaultMatchScore >= bestTextureNameMaterialRecommendationMatchScore
          ) {
            var bestTextureFileVaultMatches = this.fileVaultReferenceRecommendations
              .filter(x => this.buildAssetResourceKeyFromFileNameWithoutMaterialHint(x.fileVaultReferenceRelation.fileName!) === bestTextureFileNameFileVaultMatch);
            
            if (bestTextureFileVaultMatches.length > 0) {
              var materialRecommendation = materialRecommendationAssetInstances.find(x => x.callerReferenceKey === origCallerReferenceKey);
              if (materialRecommendation && origCallerReferenceKey) {
                assetPropertyDetails.assetProperty.callerReferenceKey = origCallerReferenceKey;
                if (!this.newAssetInstances.some(x => x.callerReferenceKey === origCallerReferenceKey)) {
                  this.newAssetInstances.push(materialRecommendation);
                }
              }

              for (let bestTextureFileVaultMatch of bestTextureFileVaultMatches) {

                await this.selectFileVaultReferenceRecommendation(bestTextureFileVaultMatch);
              }
              await this.confirmSelectedFileVaultReferenceRecommendation();
            }

          } else if (bestTextureNameNewAssetReferenceMatch &&
            bestTextureNameNewAssetReferenceMatchScore >= bestTextureNameAssetReferenceMatchScore &&
            bestTextureNameNewAssetReferenceMatchScore >= bestTextureFileNameFileVaultMatchScore &&
            bestTextureNameNewAssetReferenceMatchScore >= bestTextureNameMaterialRecommendationMatchScore
          ) {
            var bestTextureNewAssetReferenceMatches = this.newAssetReferenceRecommendations
              .filter(x => this.getCallerReferenceKeyName(x.callerReferenceKey!) === bestTextureNameNewAssetReferenceMatch);

            if (bestTextureNewAssetReferenceMatches.length > 0) {
              for (let bestTextureNewAssetReferenceMatch of bestTextureNewAssetReferenceMatches) {

                await this.selectNewAssetReferenceRecommendation(bestTextureNewAssetReferenceMatch);
              }
              await this.confirmSelectedAssetReferenceRecommendation();
            }

          } else if (bestTextureNameMaterialRecommendationMatch &&
            bestTextureNameMaterialRecommendationMatchScore >= bestTextureNameAssetReferenceMatchScore &&
            bestTextureNameMaterialRecommendationMatchScore >= bestTextureFileNameFileVaultMatchScore &&
            bestTextureNameMaterialRecommendationMatchScore >= bestTextureNameNewAssetReferenceMatchScore
          ) {
            var bestTextureMaterialRecommendationMatch = materialRecommendationAssetInstances
              .find(x => this.getCallerReferenceKeyName(x.callerReferenceKey!) === bestTextureNameMaterialRecommendationMatch);

            if (bestTextureMaterialRecommendationMatch) {
              var newAssetReferenceRecommendation = new NewAssetReferenceRecommendation();
              newAssetReferenceRecommendation.callerReferenceKey = bestTextureMaterialRecommendationMatch.callerReferenceKey;
              this.newAssetReferenceRecommendations.push(newAssetReferenceRecommendation);

              await this.selectNewAssetReferenceRecommendation(newAssetReferenceRecommendation);

              this.newAssetInstances.push(bestTextureMaterialRecommendationMatch);
              bestTextureMaterialRecommendationMatch.isSelected = true;
              await this.confirmSelectedAssetReferenceRecommendation();
            }

          } else {
            assetPropertyDetails.assetProperty.callerReferenceKey = origCallerReferenceKey;
          }

          materialRecommendationAssetInstances.filter(e => e.callerReferenceKey === assetPropertyDetails.assetProperty.callerReferenceKey).forEach(x => x.isSelected = true)
        }
      }
    } else {
      console.log("Mesh has embedded textures, Skipping adding materials.");
    }
    
    if (!assetInstance.values["Manufacturer"]) {
      assetInstance.values["Manufacturer"] = this.companyCode;
    }

    if (!assetInstance.values["SKU"]) {
      assetInstance.values["SKU"] = this.product.sku;
    }

    if (!assetInstance.values["ProductName"]) {
      assetInstance.values["ProductName"] = this.product.name;
    }

    assetInstance.values["Notes"] = await this.getProductNotes();

    this.clearCurrentSelection();
  }

  async getProductNotes(): Promise<string | undefined> {

    var getFileVaultReferenceRelationsRequest = new GetFileVaultReferenceRelationsRequest();
    getFileVaultReferenceRelationsRequest.companyId = this.product.companyId;
    getFileVaultReferenceRelationsRequest.subjectId = this.product.id;
    getFileVaultReferenceRelationsRequest.extensionsFilter = [".txt"];
    getFileVaultReferenceRelationsRequest.category = FileVaultCategory.Assets;
    var getFileVaultReferenceRelationsResult = await this.situViewClient.getFileVaultReferenceRelations(getFileVaultReferenceRelationsRequest);

    if (!getFileVaultReferenceRelationsResult.success) {
      console.log(getFileVaultReferenceRelationsResult.errorMessages);
      return undefined;
    }

    var notes = undefined;
    for (let fileVaultReferenceRelation of getFileVaultReferenceRelationsResult.fileVaultReferenceRelations!) {

      if (fileVaultReferenceRelation.fileSize! < 1024)
      {
        var fileContents = await this.situViewClient.getFile(this.product.companyId, fileVaultReferenceRelation.id);
        var text = await fileContents.data.text();

        if (!notes) {
          notes = text;
        } else {
          notes = `${notes}\n\n${text}`;
        }
        
      }
    }

    return notes;
  }

  processProductNotesForMaterial(notes: string, assetInstance: AssetInstance) {

    var tilingMatch = notes.match(/(\d+\.?\d*)\s?([cm]?m)?\s+x\s+(\d+\.?\d*)\s?([cm]?m)/i);
    var xTilingString = tilingMatch?.[1];
    var yTilingString = tilingMatch?.[3];
    if (xTilingString && yTilingString) {
      var xTilingVal = Number.parseFloat(xTilingString);
      var xTilingUnit = tilingMatch?.[2];
      var yTilingVal = Number.parseFloat(yTilingString);
      var yTilingUnit = tilingMatch?.[4];

      if (!xTilingUnit) {
        xTilingUnit = yTilingUnit
      }

      if (xTilingUnit == "cm") {
        xTilingVal = 100 / xTilingVal;
      } else if (xTilingUnit == "mm") {
        xTilingVal = 1000 / xTilingVal;
      } else {
        xTilingVal = 1 / xTilingVal;
      }

      if (yTilingUnit == "cm") {
        yTilingVal = 100 / yTilingVal;
      } else if (yTilingUnit == "mm") {
        yTilingVal = 1000 / yTilingVal;
      } else {
        yTilingVal = 1 / yTilingVal;
      }

      assetInstance.values["Tiling X"] = xTilingVal;
      assetInstance.values["Tiling Y"] = yTilingVal;
    }

    var smoothnessMatch = notes.match(/\bsmoothness\b\s?=\s?(\d.?\d*)/i);
    var smoothnessString = smoothnessMatch?.[1];
    if (smoothnessString) {
      var smoothnessVal = Number.parseFloat(smoothnessString);
      assetInstance.values["Smoothness"] = smoothnessVal;
    }

    var metallicMatch = notes.match(/\bmetallic\b\s?=\s?(\d.?\d*)/i);
    var metallicString = metallicMatch?.[1];
    if (metallicString) {
      var metallicVal = Number.parseFloat(metallicString);
      assetInstance.values["Metallic"] = metallicVal;
    }

  }

  findMostSimilarAssetReferenceRecommendations(assetReferenceRecommendations: AssetReferenceRecommendation[], productStringMatches: Set<string>): [AssetReferenceRecommendation | undefined, number] {

    var bestScore = 0;
    var bestAssetReferenceRecommendation: AssetReferenceRecommendation | undefined = undefined;

    var log = `Find Most Similar Asset Reference Recommendations\n${[...productStringMatches].join(" ")}`;

    for (let assetReferenceRecommendation of assetReferenceRecommendations) {
      var testString = this.getCallerReferenceKeyName(assetReferenceRecommendation.callerReferenceKey!);
      var score = this.calculateStringSimilarityScore(testString, productStringMatches);

      log = `${log}\n${assetReferenceRecommendation.callerReferenceKey} = ${score}`;
      if (score > bestScore) {
        bestScore = score;
        bestAssetReferenceRecommendation = assetReferenceRecommendation
      }
    }

    log = `${log}\n\nHighest Score: ${bestAssetReferenceRecommendation?.callerReferenceKey} = ${bestScore}`;
    console.log(log);
    return [bestAssetReferenceRecommendation, bestScore];
  }

  findMostSimilarFileVaultReference(fileVaultReferenceRecommendations: FileVaultReferenceRecommendation[], productStringMatches: Set<string>): [FileVaultReferenceRecommendation | undefined, number] {

    var bestScore = 0;
    var bestFileVaultReference: FileVaultReferenceRecommendation | undefined = undefined;

    var log = `Find Most Similar File Vault Reference\n${[...productStringMatches].join(" ")}`;

    for (let fileVaultReferenceRecommendation of fileVaultReferenceRecommendations) {
      var testString = this.fileNameWithoutExtension(fileVaultReferenceRecommendation.fileVaultReferenceRelation.fileName!);
      var score = this.calculateStringSimilarityScore(testString, productStringMatches);

      log = `${log}\n${fileVaultReferenceRecommendation.fileVaultReferenceRelation.fileName} = ${score}`;
      if (score > bestScore) {
        bestScore = score;
        bestFileVaultReference = fileVaultReferenceRecommendation
      }
    }

    log = `${log}\n\nHighest Score: ${bestFileVaultReference?.fileVaultReferenceRelation.fileName} = ${bestScore}`;
    console.log(log);
    return [bestFileVaultReference, bestScore];
  }

  findMostSimilarString(strings: Set<string>, productStringMatches: Set<string>): [string | undefined, number] {

    var bestScore = 0;
    var bestString: string | undefined = undefined;

    for (let testString of strings) {
      var score = this.calculateStringSimilarityScore(testString, productStringMatches);

      if (score > bestScore) {
        bestScore = score;
        bestString = testString
      }
    }

    return [bestString, bestScore];
  }

  calculateStringSimilarityScore(stringToScore: string, productStringMatches: Set<string>): number {
    stringToScore = stringToScore.toLowerCase();
    var stringPartsToScore = stringToScore.split(/[\s_\-\\/.]+/);
    var score = 0;

    for (let stringPartToScore of stringPartsToScore) {
      var i = 0;
      var scoreAdded = false;
      for (let productStringMatch of productStringMatches) {
        var scoreToAdd = 0;
        if (stringPartToScore === productStringMatch) {
          // Full score for a full match
          scoreToAdd = 1;
        } else if (stringPartToScore.includes(productStringMatch)) {
          // partial score, adjusted by the number of characters that do match
          scoreToAdd = productStringMatch.length / stringPartToScore.length;
        } else if (productStringMatch.includes(stringPartToScore)) {
          // partial score, adjusted by the number of characters that do match
          scoreToAdd = stringPartToScore.length / productStringMatch.length;
        }

        // Score the last productStringMatches higher than at the start
        if (scoreToAdd > 0) {
          var scoreScaler = 0.5 + ((i / (productStringMatches.size - 1)) / 2); // scaler will be 0.5 for the start of the list to 1 at the end
          score += scoreToAdd * scoreScaler;
          scoreAdded = true;
        }

        i++;
      }

      if (!scoreAdded) {
        score -= stringPartToScore.length / stringToScore.length; // This part of the string never scored, penalize it as a fraction of the string it makes up
      }
    }

    //console.log(`score result: ${stringToScore} = ${score} for ${productStringMatches}`);
    return score;
  }

  reduceString(input: string, maxLength: number): string {
    if (!input || input.length <= maxLength)
    {
      return input;
    }

    // Try 1: Remove one character at a time (space, -, _, \, /, .) from end to start
    const removableChars = new Set([' ', '-', '_', '\\', '/', '.']);
    let chars = input.split('');
    for (let i = chars.length - 1; i >= 0; i--) {
      if (removableChars.has(chars[i])) {
        chars.splice(i, 1);
        if (chars.length <= maxLength)
        { 
          return chars.join('');
        }
      }
    }

    // Try 2: Remove vowels from end to start
    const vowels = new Set(['a', 'e', 'i', 'o', 'u']);
    chars = input.split('');
    for (let i = chars.length - 1; i >= 0; i--) {
      if (vowels.has(chars[i].toLowerCase())) {
        chars.splice(i, 1);
        if (chars.length <= maxLength)
        {
          return chars.join('');
        }
      }
    }

    // Try 3: Remove both from end to start
    const both = new Set(...vowels, ...removableChars);
    chars = input.split('');
    for (let i = chars.length - 1; i >= 0; i--) {
      if (both.has(chars[i].toLowerCase())) {
        chars.splice(i, 1);
        if (chars.length <= maxLength)
        {
          return chars.join('');
        }
      }
    }

    // Try 4: Trim the string to fit to the word boundry as long as the word boundry is not to big
    var boundaryChars = removableChars;
    chars = input.split('');
    for (let i = maxLength; i >= maxLength - 5; i--) {
      if (boundaryChars.has(chars[i])) {
        chars = chars.slice(0, i);
        if (chars.length <= maxLength)
        {
          return chars.join('');
        }
      }
    }

    // Try 5: Trim the string to fit
    return input.slice(0, maxLength);
  }

  canBuildArtifactsFromAssets(): boolean {
    return !this.isPublishing && (
      this.newAssetInstanceTemplateName === "product" ||
      this.newAssetInstanceTemplateName === "material" ||
      this.newAssetInstanceTemplateName === "brick"
    );
  }

  clearObject(obj: Record<string, any>): void {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        delete obj[key];
      }
    }
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}

class Recommendation {
    isSelected: boolean = false;
}

class AssetReferenceRecommendation extends Recommendation {
  callerReferenceKey?: string;
  meshDetails: GetMeshDetailsResponse | undefined;

  assetValidation: { key: string; value: AssetValidationRule; }[] = [];

  onAssetValidationCompleted(result: {validationRules: { key: string; value: AssetValidationRule; }[]}) {
    this.assetValidation = result.validationRules;
  }
}

class FileVaultReferenceRecommendation extends Recommendation {
  fileVaultReferenceRelation!: FileVaultReferenceRelation;
  meshDetails: GetMeshDetailsResponse | undefined;

  assetValidation: { key: string; value: AssetValidationRule; }[] = [];

  onAssetValidationCompleted(result: {validationRules: { key: string; value: AssetValidationRule; }[]}) {
    this.assetValidation = result.validationRules;
  }
}

class NewAssetReferenceRecommendation extends AssetReferenceRecommendation {
  fileVaultReferenceRelation?: FileVaultReferenceRelation;

  assetValidation: { key: string; value: AssetValidationRule; }[] = [];

  onAssetValidationCompleted(result: {validationRules: { key: string; value: AssetValidationRule; }[]}) {
    this.assetValidation = result.validationRules;
  }
}

class AssetInstancePublishDetails {
  assetType!: PropertyType;
  name!: string;
  callerReferenceKey!: string;
  assetInstance!: AssetInstance;
  meshDetails: GetMeshDetailsResponse | undefined;
  selectionDisabled: boolean = false;
  isSelected: boolean = true;

  // Action to perform when the user confirms the selection of assets
  confirmAction?: () => void;
}

class FileVaultAssetResourcePublishDetails {
  assetType!: PropertyType;
  name!: string;
  callerReferenceKey!: string;
  fileVaultReferenceRelation!: FileVaultReferenceRelation;
  meshDetails: GetMeshDetailsResponse | undefined;
  selectionDisabled: boolean = false;
  isSelected: boolean = true;

  // Action to perform when the user confirms the selection of assets
  confirmAction?: () => void;

  assetValidation: { key: string; value: AssetValidationRule; }[] = [];

  onAssetValidationCompleted(result: {validationRules: { key: string; value: AssetValidationRule; }[]}) {
    this.assetValidation = result.validationRules;
  }
}

export type AssetInstanceValueTypes = string | number | boolean | undefined | AssetInstance | AssetReferenceValue | AssetInstanceValueTypes[] | KeyValuePair;

export class AssetReferenceValue {
  name?: string;
  callerReferenceKey?: string;
}

export class AssetPropertyDetails {
  assetProperty!: AssetReferenceValue;
  assetPropertyTemplate!: AssetTemplateProperty;

  assetInstanceKey!: string;
  assetInstance!: AssetInstance;
  assetTemplate!: AssetTemplate;
}

export class AssetInstance {
  templateID!: string;
  templateName!: string;
  values!: Record <string, AssetInstanceValueTypes>;
  isDirty: boolean = false;
}

export class AssetTemplate {
  properties!: AssetTemplateProperty[];
}

export class AssetTemplateProperty extends AssetTemplateProperties {
}

export class KeyValuePair {
  key?: string;
  value?: AssetInstanceValueTypes;
}